import { Link, router, usePage } from "@inertiajs/react"
import { UserRound, Wrench } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu"
import { settings_profile_path } from "@/lib/routes"
import type { Market } from "@/lib/useMarket"
import { buildAdminLinks, buildSettingsLinks, type NavLink } from "./navLinks"
import { TOP_BAR_CELL } from "./topBarCell"

type PageProps = {
  user?: { name: string; email: string; avatar_url?: string | null }
  remembered_market?: Market | null
  wholesale_market_ids?: string[]
  can_manage_org_nodes?: boolean
  can_manage_product_groups?: boolean
  can_manage_warehouses?: boolean
  can_manage_roles?: boolean
  is_super_admin?: boolean
}

function MenuLink({ link }: { link: NavLink }) {
  const Icon = link.icon
  return (
    <DropdownMenuItem asChild>
      <Link href={link.href} className="gap-2">
        {Icon && <Icon size={14} />}
        {link.label}
      </Link>
    </DropdownMenuItem>
  )
}

// The avatar at the right end of the top bar. Its menu starts with who is
// logged in, then the profile, the org settings, internal tools (Super Admins
// only; hovering Admin opens its pages) and logout.
export default function UserMenu() {
  const { props } = usePage<PageProps>()
  const user = props.user
  const market = props.remembered_market ?? null
  const settings = buildSettingsLinks({
    market,
    // Settings links open one market only; buildSettingsLinks does not read this.
    allMarketsPicked: false,
    wholesaleMarketIds: props.wholesale_market_ids ?? [],
    canManageOrgNodes: Boolean(props.can_manage_org_nodes),
    canManageProductGroups: Boolean(props.can_manage_product_groups),
    canManageWarehouses: Boolean(props.can_manage_warehouses),
    canManageRoles: Boolean(props.can_manage_roles),
  })

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className={TOP_BAR_CELL} aria-label="Your account">
          {user?.avatar_url ? (
            <img src={user.avatar_url} alt="" className="h-7 w-7 rounded-md object-cover" />
          ) : (
            <div
              className="flex h-7 w-7 items-center justify-center rounded-md text-xs font-semibold text-background"
              style={{ background: "linear-gradient(135deg, var(--chart-2), var(--secondary))" }}
            >
              {user?.name ? user.name.charAt(0).toUpperCase() : user?.email?.charAt(0).toUpperCase() || "?"}
            </div>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[220px]">
        {user && (
          <>
            <div className="px-2 py-1.5">
              <div className="truncate text-sm font-medium text-foreground">{user.name || user.email}</div>
              {user.name && <div className="truncate text-xs text-muted-foreground">{user.email}</div>}
            </div>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuLabel>Personal</DropdownMenuLabel>
        <DropdownMenuItem asChild>
          <Link href={settings_profile_path()} className="gap-2">
            <UserRound size={14} />
            Your profile
          </Link>
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuLabel>Settings</DropdownMenuLabel>
        {settings.map((link) => <MenuLink key={link.label} link={link} />)}

        {props.is_super_admin && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Internal</DropdownMenuLabel>
            <DropdownMenuItem asChild>
              <a href="/sidekiq" target="_blank" rel="noopener noreferrer" className="gap-2">
                <span>⚙</span>
                Sidekiq
              </a>
            </DropdownMenuItem>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="gap-2">
                <Wrench size={14} />
                Admin
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="min-w-[180px]">
                {buildAdminLinks().map((link) => <MenuLink key={link.label} link={link} />)}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </>
        )}

        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => router.delete("/logout")} className="gap-2 text-destructive">
          <span>⏻</span>
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
